import type { MeEntitlements, MePlanState } from "@/features/auth/hooks/useCurrentUser"
import { FEATURE_LABEL } from "@/features/nira2/matter/features"
import type { Plan } from "./plan"

/** [one, many], named as the product names its features (FEATURE_LABEL), so a draft here reads as it does in the app. */
const NOUNS: Record<string, [string, string]> = {
  "nira.message": ["research message", "research messages"],
  "oio_sim.draft": [`${FEATURE_LABEL.oio} draft`, `${FEATURE_LABEL.oio} drafts`],
  "oia_sim.draft": [FEATURE_LABEL.oia, FEATURE_LABEL.oia],
  "nira.officer_reading": ["officer's reading of a notice", "officer's readings of a notice"],
  "nira.notice_reading": ["notice analysis", "notice analyses"],
  "nira.appeal_reading": ["Appeal or not reading", "Appeal or not readings"],
  "nira.rerun_documents": ["re-run with documents", "re-runs with documents"],
  "nira.fact_rerun": ["fact re-run", "fact re-runs"],
  "nira.department_reading": ["department's reading", "department's readings"],
  "order.draft": ["order draft", "order drafts"],
  "order.draft_edit": ["draft edit", "draft edits"],
}
const noun = (metric: string, n: number): string | undefined => NOUNS[metric]?.[n === 1 ? 0 : 1]
const LABELS: Record<string, string> = Object.fromEntries(Object.entries(NOUNS).map(([k, v]) => [k, v[1]]))

export const metricLabel = (metric: string): string => LABELS[metric] ?? "allowance"

/** "a day", "a month", "in each draft"; "" for lifetime. */
const PER: Record<string, string> = { day: "a day", month: "a month", draft: "per draft" }

/** The plan's name in the app: "Nira Plus", "Nira Pro", "Free, first month", "Free". */
export function planName(state: MePlanState | null | undefined, subscribed = false): string {
  switch (state) {
    case "exempt": return "Unlimited (staff)"
    case "free_month": return "Free, first month"
    case "plus": return "Nira Plus"
    case "pro": return "Nira Pro"
    case "grace": return "Nira Pro, payment due"
    case "free": case "guest": return "Free"
    default: return subscribed ? "Nira Pro" : "Free"
  }
}

const PRO_LINES = [
  "Notice analysis and Appeal or not on every notice and order",
  "The officer's reading of each allegation",
  "Daily research messages",
  "Order drafts, with edits",
]

/** One allowance as a line: "10 research messages a day". */
export function allowanceLine(metric: string, quantity: number | null, window: string): string | null {
  const n = noun(metric, quantity ?? 2)
  if (!n) return null
  const per = PER[window]
  return `${quantity === null ? "Any number of" : quantity} ${n}${per ? ` ${per}` : ""}`
}

/**
 * What a plan gives. With the plan list loaded, its own allowances; without it, the current plan's
 * numbers for a subscriber, or the allowance types (no invented figures) for everyone else.
 */
export function allowanceLines(e: MeEntitlements | null, plan: Plan | null = null): string[] {
  if (plan?.allowances.length) {
    return plan.allowances.flatMap((a) => {
      const l = allowanceLine(a.metric, a.quantity, a.window)
      return l ? [l] : []
    })
  }
  if (!e?.subscribed) return PRO_LINES
  return e.metrics.flatMap((m) => {
    if (m.limit === null) return []
    const l = allowanceLine(m.metric, m.limit, m.window)
    return l ? [l] : []
  })
}

/** What Pro gives, as the price list says it, for when the plan list has not loaded. */
export const PRO_FALLBACK_LINES = [
  "10 research messages a day",
  "5 notice analyses a day",
  "3 Appeal or not checks a day",
  "10 re-runs with documents a day",
  "The department's reading on every question",
  "1 order draft a month, with 10 edits",
]

/** What Plus gives: its questions, plus everything in Free. */
export function plusLines(plan: Plan | null): string[] {
  const q = plan?.allowances.find((a) => a.metric === "nira.message")?.quantity ?? 40
  return [`${q} questions a month`, "Everything in Free"]
}
